/**
 * useDrawingState Hook
 *
 * Manages the state for drawing new polygon regions on the floor plan.
 * Used by the map editor to track the current polygon being drawn.
 *
 * Features:
 * - Track currently selected wall section
 * - Track drawing mode (isDrawing)
 * - Maintain current polygon points being drawn
 * - Add/remove points during drawing
 * - Finish drawing to produce a completed polygon
 * - Cancel drawing to reset state
 *
 * @example
 * ```tsx
 * const {
 *   isDrawing,
 *   currentPolygon,
 *   selectedWallSection,
 *   startDrawing,
 *   addPoint,
 *   finishDrawing,
 *   cancelDrawing,
 * } = useDrawingState();
 *
 * // Start drawing for a wall section
 * startDrawing('Main Wall');
 *
 * // Add points as user clicks
 * addPoint({ x: 0.1, y: 0.2 });
 *
 * // Finish and get the completed polygon
 * const result = finishDrawing();
 * if (result) {
 *   console.log(result.wallSectionName, result.polygon);
 * }
 * ```
 */

import { useState, useCallback, useMemo } from 'react';
import type { Point } from '@flash/shared';

/**
 * Return type for finishDrawing - the completed polygon with wall section name
 */
export interface FinishedPolygon {
  /** The wall section this polygon belongs to */
  wallSectionName: string;
  /** The polygon vertices in normalized coordinates (0-1) */
  polygon: Point[];
}

/**
 * Return value from useDrawingState hook
 */
export interface UseDrawingStateResult {
  /** Whether the user is currently in drawing mode */
  isDrawing: boolean;

  /** The points of the polygon being drawn (normalized 0-1 coordinates) */
  currentPolygon: Point[];

  /** The wall section currently being drawn for, or null if not drawing */
  selectedWallSection: string | null;

  /** Whether the current polygon has enough points to be finished (>= 3) */
  canFinishDrawing: boolean;

  /** Start drawing a polygon for the specified wall section */
  startDrawing: (wallSectionName: string) => void;

  /** Add a point to the current polygon (only works if isDrawing is true) */
  addPoint: (point: Point) => void;

  /** Remove the last point from the current polygon */
  removeLastPoint: () => void;

  /** Finish drawing and return the completed polygon, or null if invalid */
  finishDrawing: () => FinishedPolygon | null;

  /** Cancel drawing and reset all state */
  cancelDrawing: () => void;
}

/** Minimum number of points required for a valid polygon */
const MIN_POLYGON_POINTS = 3;

/**
 * Hook for managing polygon drawing state
 *
 * @returns Drawing state and control functions
 */
export const useDrawingState = (): UseDrawingStateResult => {
  // Drawing mode flag
  const [isDrawing, setIsDrawing] = useState(false);

  // Current polygon points being drawn
  const [currentPolygon, setCurrentPolygon] = useState<Point[]>([]);

  // Currently selected wall section name
  const [selectedWallSection, setSelectedWallSection] = useState<string | null>(null);

  /**
   * Start drawing a polygon for a wall section
   */
  const startDrawing = useCallback((wallSectionName: string) => {
    setIsDrawing(true);
    setCurrentPolygon([]);
    setSelectedWallSection(wallSectionName);
  }, []);

  /**
   * Add a point to the current polygon
   * Only works when in drawing mode
   */
  const addPoint = useCallback((point: Point) => {
    setCurrentPolygon((prev) => {
      // Only add points when in drawing mode (checked via state)
      // This is handled implicitly - if not drawing, this does nothing meaningful
      return [...prev, point];
    });
  }, []);

  /**
   * Remove the last point from the current polygon
   */
  const removeLastPoint = useCallback(() => {
    setCurrentPolygon((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -1);
    });
  }, []);

  /**
   * Cancel drawing and reset all state
   */
  const cancelDrawing = useCallback(() => {
    setIsDrawing(false);
    setCurrentPolygon([]);
    setSelectedWallSection(null);
  }, []);

  /**
   * Finish drawing and return the completed polygon
   * Returns null if polygon is invalid (fewer than 3 points) or not drawing
   */
  const finishDrawing = useCallback((): FinishedPolygon | null => {
    // Capture current state before resetting
    const wallSection = selectedWallSection;
    const polygon = currentPolygon;
    const drawing = isDrawing;

    // Reset state first
    setIsDrawing(false);
    setCurrentPolygon([]);
    setSelectedWallSection(null);

    // Return null if not in valid drawing state
    if (!drawing || !wallSection) {
      return null;
    }

    // Return null if polygon doesn't have enough points
    if (polygon.length < MIN_POLYGON_POINTS) {
      return null;
    }

    return {
      wallSectionName: wallSection,
      polygon: polygon,
    };
  }, [isDrawing, currentPolygon, selectedWallSection]);

  /**
   * Whether the current polygon can be finished (has at least 3 points)
   */
  const canFinishDrawing = useMemo(() => {
    return isDrawing && currentPolygon.length >= MIN_POLYGON_POINTS;
  }, [isDrawing, currentPolygon.length]);

  // Return modified addPoint that checks isDrawing
  const safeAddPoint = useCallback(
    (point: Point) => {
      if (!isDrawing) return;
      addPoint(point);
    },
    [isDrawing, addPoint]
  );

  return {
    isDrawing,
    currentPolygon,
    selectedWallSection,
    canFinishDrawing,
    startDrawing,
    addPoint: safeAddPoint,
    removeLastPoint,
    finishDrawing,
    cancelDrawing,
  };
};

export default useDrawingState;
